import { BRISBANE, dayLabel, formatTime, zoneLabel } from '../lib/timezones';
import type { AvailabilitySlot } from '../hooks/use-public-booking';

/**
 * The parts the booking page and the reference page both draw.
 *
 * These lived inside BookPage until the reference page needed the same day strip and the
 * same slot grid to move a booking. Copying them would have meant two grids drifting
 * apart, and the one that mattered would have been the one nobody looked at.
 *
 * Still inline styles and still no CSS framework, because the site has none and this
 * mounts inside its chrome. The one thing that is NOT inline is the two-column layout,
 * which lives in styles.css: a media query cannot be expressed as a style attribute, and
 * faking it with matchMedia would put a resize listener and a re-render in front of a
 * layout the browser can do on its own.
 */

export const NAVY = '#0C2030';
export const SKY = '#43ABDB';
export const DEEP = '#041A2B';
export const INK = '#2A3B4A';
export const MUTED = '#5F7181';
export const CORAL = '#FF5151';
export const LINE = '#DCE6EC';

export const CONTACT_EMAIL = 'todd@innovatexsolutions.com.au';

/** The booking type /book/ lands on when the URL names none. */
export const DEFAULT_SLUG = 'intro-30';

/**
 * The host's photo, beside the title rather than above it.
 *
 * `objectPosition` is pushed to 70% on purpose. The branded headshot carries the
 * InnovateX mark in its top-left corner and the face sits low in a 360x450 frame, so
 * a centred square crop clips the chin and drags a slice of the logo into the circle.
 * 70% was picked by rendering 28/55/70/85/100 and looking: below ~55 the mark is still
 * in frame, above ~85 the face rides high with a lot of chest. 70 centres the face.
 *
 * Absent photo renders nothing at all. A booking type with no host, or a host with no
 * picture, is a normal state rather than a broken one.
 */
export function HostAvatar({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      width={56}
      height={56}
      loading="lazy"
      style={{
        width: 56, height: 56, flex: '0 0 56px', borderRadius: '50%',
        objectFit: 'cover', objectPosition: '50% 70%', display: 'block',
      }}
    />
  );
}

export function Shell({
  title, photoUrl, children,
}: { title?: string; photoUrl?: string | null; children: React.ReactNode }) {
  return (
    <div style={{ color: INK, fontFamily: 'Manrope, system-ui, sans-serif' }}>
      {title ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '0 0 16px' }}>
          {photoUrl ? <HostAvatar src={photoUrl} /> : null}
          {/* h2: the host page (layouts/book/list.html) owns the page's h1. */}
          <h2 style={{ margin: 0, fontSize: 26, lineHeight: 1.25, fontWeight: 600, color: NAVY }}>{title}</h2>
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function Notice({ heading, message, action }: { heading: string; message: string; action?: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <h2 style={{ margin: '0 0 10px', fontSize: 19, fontWeight: 600, color: NAVY }}>{heading}</h2>
      <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.6, color: MUTED }}>{message}</p>
      {action}
    </div>
  );
}

export function PrimaryButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      style={{
        width: '100%', minHeight: 52, border: 'none', borderRadius: 999,
        background: props.disabled ? '#B3BDC2' : NAVY, color: '#FFFFFF',
        fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        ...props.style,
      }}
    >
      {children}
    </button>
  );
}

export const linkButtonStyle = {
  display: 'block', minHeight: 48, lineHeight: '48px', textAlign: 'center' as const,
  background: SKY, color: DEEP, borderRadius: 8, fontWeight: 600, textDecoration: 'none',
};

export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ margin: '0 0 10px', fontSize: 17, fontWeight: 600, color: NAVY }}>{children}</h2>
  );
}

/** One local day of slots, after the API's Brisbane grouping has been redone. */
export interface LocalDay {
  /** Date key in the DISPLAYED zone, which is not always the Brisbane one. */
  date: string;
  /** The first slot of the day, used to label the tile in the displayed zone. */
  labelAt: number;
  slots: AvailabilitySlot[];
}

export function DayStrip(
  { days, selected, zone, onSelect }: {
    days: LocalDay[]; selected: string | null; zone: string; onSelect: (date: string) => void;
  },
) {
  return (
    <section aria-label="Pick a day">
      <SectionHeading>Pick a day</SectionHeading>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
        {days.map((day) => {
          const label = dayLabel(day.labelAt, zone);
          const isOn = day.date === selected;
          return (
            <button
              key={day.date}
              type="button"
              onClick={() => onSelect(day.date)}
              aria-pressed={isOn}
              // The name starts with the visible text so voice control users can say what they see (WCAG 2.5.3).
              aria-label={`${label.weekday} ${label.day} ${label.month}, ${label.full}`}
              style={{
                flex: '0 0 auto', minWidth: 64, minHeight: 72, borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${isOn ? SKY : LINE}`,
                background: isOn ? NAVY : '#FFFFFF',
                color: isOn ? '#FFFFFF' : INK,
                fontFamily: 'inherit', padding: '8px 4px',
              }}
            >
              <span style={{ display: 'block', fontSize: 12, opacity: 0.8 }}>{label.weekday}</span>
              <span style={{ display: 'block', fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{label.day}</span>
              <span style={{ display: 'block', fontSize: 12, opacity: 0.8 }}>{label.month}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function SlotGrid(
  { day, selected, zone, onSelect }: {
    day: LocalDay | null; selected: AvailabilitySlot | null; zone: string;
    onSelect: (s: AvailabilitySlot) => void;
  },
) {
  if (!day) return null;

  return (
    <section aria-label="Pick a time" style={{ marginTop: 24 }}>
      <SectionHeading>Pick a time</SectionHeading>
      {day.slots.length === 0 ? (
        <p style={{ margin: 0, fontSize: 15, color: MUTED }}>Nothing free on that day. Try another one.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
          {day.slots.map((slot) => {
            const isOn = slot.token === selected?.token;
            return (
              <button
                key={slot.token}
                type="button"
                onClick={() => onSelect(slot)}
                aria-pressed={isOn}
                style={{
                  minHeight: 48, borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 15, fontWeight: 600,
                  border: `1px solid ${isOn ? SKY : LINE}`,
                  background: isOn ? SKY : '#FFFFFF',
                  color: isOn ? DEEP : INK,
                }}
              >
                {formatTime(slot.starts_at, zone)}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Which clock the times are in, said out loud, with a way to change it.
 *
 * Silently converting is worse than not converting. Somebody who has been told a time in
 * Brisbane time on a poster, and then reads a different number here with no label, has
 * no way to know which one to trust. So the zone is named either way, and the other one
 * is one tap away.
 *
 * Rendered as nothing at all when the visitor's zone reads the same clock as Brisbane,
 * which covers Sydney and Melbourne for half the year. There is no choice to offer when
 * both options print the same number.
 */
export function TimezoneChoice(
  { zone, visitorZone, onChange }: {
    zone: string; visitorZone: string; onChange: (tz: string) => void;
  },
) {
  const showingBrisbane = zone === BRISBANE;
  const other = showingBrisbane ? visitorZone : BRISBANE;

  return (
    <p style={{ margin: '0 0 20px', fontSize: 14, color: MUTED }}>
      {`Times shown in ${zoneLabel(zone)} time. `}
      <button
        type="button"
        onClick={() => onChange(other)}
        style={{
          border: 'none', background: 'none', padding: 0, font: 'inherit',
          color: NAVY, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer',
        }}
      >
        {`Show ${zoneLabel(other)} time`}
      </button>
    </p>
  );
}
